import { type POI, TIME_RANGES } from "@poi-planner/shared";
import { parseTimeToMinutes } from "./format";

export interface ConstraintViolation {
  type: "availability" | "idealDay" | "idealTime";
  message: string;
}

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function formatStartTime12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
}

/**
 * Check constraint violations for a POI scheduled on a specific date.
 */
export function checkConstraintViolations(
  poi: POI,
  date: string, // YYYY-MM-DD of the day the POI is scheduled on
  startTime: string | null, // wall-clock start time in the block's zone, HH:mm
): ConstraintViolation[] {
  const violations: ConstraintViolation[] = [];

  // 1. Availability window check
  if (poi.availability) {
    const dayDate = new Date(date + "T00:00:00");
    const availStart = new Date(poi.availability.start);
    const availEnd = new Date(poi.availability.end);
    // Normalize to midnight for date-only comparison
    availStart.setHours(0, 0, 0, 0);
    availEnd.setHours(23, 59, 59, 999);

    if (dayDate < availStart || dayDate > availEnd) {
      const startStr = availStart.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const endStr = availEnd.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      violations.push({
        type: "availability",
        message: `Only available ${startStr} - ${endStr}`,
      });
    }
  }

  // 2. Ideal days check
  if (poi.idealDays && poi.idealDays.length > 0) {
    const dayDate = new Date(date + "T00:00:00");
    const dayOfWeek = DAY_NAMES[dayDate.getDay()];
    const normalizedIdealDays = poi.idealDays.map((d) => d.toLowerCase());

    if (!normalizedIdealDays.includes(dayOfWeek)) {
      const preferred = poi.idealDays
        .map((d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase())
        .join(", ");
      violations.push({
        type: "idealDay",
        message: `Best on ${preferred}`,
      });
    }
  }

  // 3. Ideal time of day check
  if (poi.idealTimeOfDay && startTime) {
    const minutes = parseTimeToMinutes(startTime);
    const range = TIME_RANGES[poi.idealTimeOfDay];
    if (range && (minutes < range.start || minutes >= range.end)) {
      violations.push({
        type: "idealTime",
        message: `Best in the ${range.label}, scheduled at ${formatStartTime12h(startTime)}`,
      });
    }
  }

  return violations;
}
